import React, { useEffect, useState } from 'react';
import { wsClient } from '../services/websocketClient';
import { t, Language } from '../data/translations';

interface ReconnectionOverlayProps {
    status: 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'RECONNECTING' | 'FAILED';
    language: Language;
    onExit: () => void;
}

const ReconnectionOverlay: React.FC<ReconnectionOverlayProps> = ({ status, language, onExit }) => {
    const [attempt, setAttempt] = useState(1);
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (status === 'RECONNECTING') {
            setAttempt(wsClient.reconnectAttempts);
        }
        if (status === 'CONNECTING') {
            setAttempt(wsClient.reconnectAttempts);
        }
        if (status === 'CONNECTED' && !showSuccess) {
            // Start a small timer to show success message before unmounting? 
            // The parent controls mounting. We can just render success state.
            setShowSuccess(true);
        }
        if (status !== 'CONNECTED') {
            setShowSuccess(false);
        }
    }, [status]);

    if (status === 'CONNECTED' && !showSuccess) return null; // Should be unmounted by parent usually

    if (status === 'DISCONNECTED' && !showSuccess) {
        // transient state before reconnect?
        return null;
    }

    if ((status === 'RECONNECTING' || status === 'CONNECTING') && attempt < 4 && !showSuccess) {
        return null;
    }

    // If we are showing success, we are technically CONNECTED
    if (showSuccess) {
        return (
            <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center pointer-events-auto">
                <div className="text-center font-cinzel text-white animate-fade-in-up">
                    <div className="mb-4">
                        <svg className="w-16 h-16 mx-auto text-green-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl text-green-400 mb-2">
                        {language === 'ru' ? 'Подключение восстановлено!' : 'Connection Restored!'}
                    </h2>
                    <p className="text-gray-300">
                        {language === 'ru' ? 'Возврат в игру...' : 'Returning to game...'}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md flex items-center justify-center pointer-events-auto">
            <div className="text-center font-cinzel text-white max-w-md p-8 border border-white/10 rounded-xl bg-black/50 shadow-2xl">

                {status === 'RECONNECTING' || status === 'CONNECTING' ? (
                    <>
                        <div className="mb-6">
                            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        </div>
                        <h2 className="text-xl text-blue-400 mb-2">
                            {language === 'ru' ? 'Разрыв соединения' : 'Connection Lost'}
                        </h2>
                        <p className="text-gray-300 mb-4">
                            {language === 'ru' ? `Попытка переподключения ${attempt}/5...` : `Reconnecting ${attempt}/5...`}
                        </p>
                    </>
                ) : status === 'FAILED' || status === 'ERROR' ? (
                    <>
                        <div className="mb-6">
                            <svg className="w-12 h-12 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-xl text-red-500 mb-2">
                            {language === 'ru' ? 'Не удалось подключиться' : 'Connection Failed'}
                        </h2>
                        <p className="text-gray-400 mb-6">
                            {language === 'ru' ? 'Сервер недоступен.' : 'Server is unreachable.'}
                        </p>
                        <button
                            onClick={onExit}
                            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded shadow-lg transition-colors duration-200 border border-red-400/30"
                        >
                            {language === 'ru' ? 'Выйти в меню' : 'Exit to Menu'}
                        </button>
                    </>
                ) : null}

            </div>
        </div>
    );
};

export default ReconnectionOverlay;
